import { X } from "lucide-react";
import { useState, type FC, type KeyboardEvent } from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { parseTags } from "../model";

type Props = {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
};

/**
 * Free-form tags: type, press Enter or comma to add; Backspace on an empty
 * field removes the last one. Tags are the firm's own words, not a taxonomy.
 */
export const TagInput: FC<Props> = ({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  id
}) => {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const added = parseTags(draft).filter((tag) => !value.includes(tag));
    if (added.length) onChange([...value, ...added]);
    setDraft("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
    } else if (event.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-xs focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50",
        disabled && "opacity-60",
        className
      )}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-fill-1 px-2 py-0.5 text-xs leading-4"
        >
          {tag}
          {!disabled ? (
            <button
              type="button"
              aria-label={`Remove tag ${tag}`}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onChange(value.filter((t) => t !== tag))}
            >
              <X className="size-3" />
            </button>
          ) : null}
        </span>
      ))}
      <Input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={value.length ? "" : placeholder ?? "Add a tag"}
        className="h-6 min-w-[8rem] flex-1 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0"
      />
    </div>
  );
};
